import { OverrideImpactInputs, OverrideImpactRow } from '@features/suggested-price-column/types/override-impact';
import {
  AGGRESSIVE_OVERRIDE_THRESHOLD_PERCENT,
  isAggressiveOverride,
  isConservativeOverride,
  percentBelowAiSuggestion,
} from '@features/suggested-price-column/utils/aggressive-override';
import { displayCurrencyWithPrecision } from '@utils/currency';

const PERCENT_DECIMALS = 1;

const formatPercent = (value: number) => `${Math.abs(value).toFixed(PERCENT_DECIMALS)}%`;

/**
 * Rounds a figure to the precision it will be rendered at.
 *
 * Every sentence variant below is chosen from a delta, and a delta is only ever `0` when two
 * doubles are bit-identical. The grid shows prices rounded, so a buyer who types the NSP they
 * can see routinely stores a value that differs from it in digits no one displays — which rendered
 * "0.0%" under a sentence claiming savings. Branching on the rounded figure keeps the words and the
 * number telling the same story.
 */
const roundForDisplay = (value: number, decimals: number) => Number(value.toFixed(decimals));

const savingsPercent = (price: number, nsp: number) => ((nsp - price) / nsp) * 100;

const money = ({ currency, pricePrecision }: OverrideImpactInputs, value: number) => (
  displayCurrencyWithPrecision(Math.abs(value), currency, pricePrecision)
);

/**
 * How far the override sits from the AI suggestion, per unit. Positive means the override is
 * below it — a deeper discount. Null when either figure is missing.
 */
const unitDelta = ({ aiValue, overrideValue }: OverrideImpactInputs): number | null => (
  aiValue == null || overrideValue == null ? null : aiValue - overrideValue
);

/** Picks a sentence variant from the sign of a delta, without nesting ternaries. */
const variantForDelta = (
  delta: number,
  variants: { less: string; match: string; more: string },
): string => {
  if (delta === 0) return variants.match;

  return delta > 0 ? variants.more : variants.less;
};

/**
 * Whether the override lands on the AI suggestion once both are rounded for display. Three of the
 * rows change what they say in that case, so they all read it from here rather than each deciding
 * what "the same price" means.
 */
const matchesAiSuggestion = (inputs: OverrideImpactInputs): boolean => {
  const delta = unitDelta(inputs);

  return delta != null && roundForDisplay(delta, inputs.pricePrecision) === 0;
};

const vsAiRow = (inputs: OverrideImpactInputs): OverrideImpactRow | null => {
  const delta = unitDelta(inputs);
  const percentBelow = percentBelowAiSuggestion(inputs.overrideValue, inputs.aiValue);

  if (delta == null || percentBelow == null) return null;

  return {
    copyKey: variantForDelta(roundForDisplay(delta, inputs.pricePrecision), {
      less: 'vs_ai_less_aggressive',
      match: 'vs_ai_match',
      more: 'vs_ai_more_aggressive',
    }),
    copyParams: { amount: money(inputs, delta), percent: formatPercent(percentBelow) },
    key: 'vs_ai',
  };
};

/**
 * Three-way, because the risk differs by direction: far below the suggestion and suppliers may
 * refuse it; far above and they anchor to a generous starting point. Both share one threshold, so
 * the banding stays symmetric and matches the cell's warning icon on the low side.
 */
const supplierPerceptionRow = (inputs: OverrideImpactInputs): OverrideImpactRow | null => {
  const { aiValue, overrideValue } = inputs;

  if (percentBelowAiSuggestion(overrideValue, aiValue) == null) return null;

  const copyKey = () => {
    if (matchesAiSuggestion(inputs)) return 'supplier_perception_match';
    if (isAggressiveOverride(overrideValue, aiValue)) return 'supplier_perception_far_below';
    if (isConservativeOverride(overrideValue, aiValue)) return 'supplier_perception_far_above';

    return 'supplier_perception_within_range';
  };

  return {
    copyKey: copyKey(),
    copyParams: { threshold: `${AGGRESSIVE_OVERRIDE_THRESHOLD_PERCENT}%` },
    key: 'supplier_perception',
  };
};

/**
 * Directional, because an override can sit above the NSP as readily as below it — nothing
 * bounds the column's numeric validator, and `isConservativeOverride` exists for exactly that
 * shape. `savingsPercent` is signed and `formatPercent` is not, so without a variant per direction
 * a premium renders as a discount of the same magnitude.
 *
 * The variant is picked from the rounded percent, so an override that lands on the NSP once
 * displayed reads as a match rather than as 0.0% of savings — see `roundForDisplay`. An override
 * that instead lands on the AI suggestion gets its own sentence: comparing the two rates is
 * pointless when they are the same number.
 *
 * `aiPercent` is the one figure still assumed to be savings. An AI suggestion above the NSP
 * would mislabel itself here, which needs the model to recommend paying more than the current
 * negotiating position — far rarer than a buyer typing a high override, and not worth nine
 * sentences to cover.
 */
const savingsVsBaselineRow = (inputs: OverrideImpactInputs): OverrideImpactRow | null => {
  const { aiValue, negotiationStartingPrice: nsp, overrideValue } = inputs;

  if (aiValue == null || overrideValue == null || nsp == null || nsp <= 0) return null;

  const overrideSavings = roundForDisplay(savingsPercent(overrideValue, nsp), PERCENT_DECIMALS);

  const copyKey = () => {
    if (overrideSavings === 0) return 'savings_vs_baseline_match';
    if (matchesAiSuggestion(inputs)) return 'savings_vs_baseline_ai_match';

    return variantForDelta(overrideSavings, {
      less: 'savings_vs_baseline_premium',
      match: 'savings_vs_baseline_match',
      more: 'savings_vs_baseline_targets',
    });
  };

  return {
    copyKey: copyKey(),
    copyParams: {
      aiPercent: formatPercent(savingsPercent(aiValue, nsp)),
      overridePercent: formatPercent(savingsPercent(overrideValue, nsp)),
    },
    key: 'savings_vs_baseline',
  };
};

/**
 * Turns the figures already on a line item into the overridden drawer's "Impact of this change"
 * rows, in render order.
 *
 * Nothing here comes from the explanation endpoint: that service explains the AI's own price, so
 * it has nothing to say about a buyer's override. These three comparisons derive from the override,
 * the AI suggestion and the Negotiation Starting Price (NSP) — all already on the grid, which is why an overridden row
 * never needs to fetch.
 *
 * A row is dropped rather than guessed at when its inputs are missing (no AI suggestion yet, a zero
 * NSP), so the drawer never shows an invented number. "Supplier perception" bands the override
 * with the same threshold that raises the cell's warning icon.
 *
 * Returns plain data — i18n keys plus already-formatted values — so the component only translates
 * and renders, and the arithmetic is testable without React.
 */
export const buildOverrideImpactRows = (inputs: OverrideImpactInputs): OverrideImpactRow[] => [
  vsAiRow(inputs),
  supplierPerceptionRow(inputs),
  savingsVsBaselineRow(inputs),
].filter((row): row is OverrideImpactRow => row !== null);
